// React / Next
import { useState } from 'react';

// Components
import { Typography } from '@archieai/ui';
import { CTAButton } from '../ui/CTAButton/CTAButton';

// Hooks
import {
  useHubSpotForm,
  HUBSPOT_NEWSLETTER_CONFIG,
} from '../../hooks/useHubSpotForm';

// Utils
import { isPersonalEmail } from '../../utils/emailValidation';

export const NewsletterSection = () => {
  const [email, setEmail] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  const { submit, isSubmitting, isSuccess, error, reset } = useHubSpotForm(
    HUBSPOT_NEWSLETTER_CONFIG
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setValidationError(null);

    if (!email.trim()) return;

    if (isPersonalEmail(email)) {
      setValidationError('Please use your work email address.');
      return;
    }

    const success = await submit([{ name: 'email', value: email }]);

    if (success) {
      setEmail('');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setValidationError(null);
    // Reset error state when user starts typing again
    if (error) {
      reset();
    }
  };

  const displayError = validationError || error;

  // Success state
  if (isSuccess) {
    return (
      <section className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-64)] w-full max-md:px-[var(--spacing-16)] max-md:py-[var(--spacing-48)]">
        <div className="flex flex-col items-center w-full max-w-[704px] p-[var(--spacing-32)] bg-white border border-gray-200 rounded-[var(--radius-12)] shadow-background max-md:p-[var(--spacing-24)]">
          <div className="flex flex-col items-center gap-[var(--spacing-16)] text-center">
            <div className="w-10 h-10 rounded-full bg-green-1000/20 flex items-center justify-center">
              <svg
                width="32"
                height="32"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className="text-green-1000"
                aria-hidden="true"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
            <Typography as="h2" variant="h3" className="text-blue-1000">
              You Are In!
            </Typography>
            <Typography as="p" variant="small" className="text-blue-1000">
              Thanks for subscribing. We'll keep you updated with the Archie's
              latest news.
            </Typography>
            <button
              type="button"
              onClick={reset}
              className="font-body text-[14px] leading-[20px] text-orange-800 hover:text-orange-1000 underline underline-offset-2 transition-colors"
            >
              Subscribe another email
            </button>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="flex justify-center px-[var(--spacing-24)] py-[var(--spacing-64)] w-full max-md:px-[var(--spacing-16)] max-md:py-[var(--spacing-48)]">
      <div className="flex flex-col items-start w-full max-w-[704px] p-[var(--spacing-32)] bg-white border border-gray-200 rounded-[var(--radius-12)] shadow-background max-md:p-[var(--spacing-24)]">
        <div className="mb-[var(--spacing-24)]">
          <Typography variant="column-header" className="text-blue-800">
            NEWSLETTER
          </Typography>
        </div>

        <div className="flex flex-col gap-[var(--spacing-24)] w-full">
          <div className="flex flex-col gap-[var(--spacing-8)]">
            <Typography as="h2" variant="h3" className="text-blue-1000">
              Stay In The Loop.
            </Typography>
            <Typography
              as="p"
              variant="small"
              className="text-blue-1000 max-w-[600px]"
            >
              Be the first to hear product updates, new features, and launch
              news. No noise.
            </Typography>
          </div>

          <form
            className="flex flex-col gap-[var(--spacing-12)] w-full"
            onSubmit={handleSubmit}
          >
            <div className="flex gap-[var(--spacing-16)] w-full max-md:flex-col">
              <div
                className={`
                  relative flex-1 h-[56px]
                  border rounded-[var(--radius-8)]
                  transition-colors duration-200
                  ${displayError ? 'border-red-1000' : isFocused ? 'border-orange-800' : 'border-gray-200'}
                `}
              >
                <input
                  type="email"
                  placeholder="Work email address"
                  value={email}
                  onChange={handleEmailChange}
                  onFocus={() => setIsFocused(true)}
                  onBlur={() => setIsFocused(false)}
                  disabled={isSubmitting}
                  className="w-full h-full px-[var(--spacing-16)] bg-white rounded-[var(--radius-8)] outline-none font-body text-[16px] leading-[24px] text-blue-1000 placeholder:text-gray-600 disabled:opacity-50"
                  aria-label="Work email address"
                  aria-invalid={!!displayError}
                  aria-describedby={displayError ? 'newsletter-error' : undefined}
                />
              </div>
              <CTAButton type="submit" disabled={isSubmitting || !email.trim()}>
                {isSubmitting ? 'Subscribing...' : 'Subscribe'}
              </CTAButton>
            </div>

            {displayError && (
              <p
                id="newsletter-error"
                className="font-body text-[14px] leading-[20px] text-red-1000"
                role="alert"
              >
                {displayError}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};
